/**
 * The served ad's type environment, for the React preview (render parity Stage 0).
 *
 * The served tag (backend `creativeTemplate.generateTag.model.js`) wraps every ad in a `<style>`
 * block: an inlined Roboto Condensed WOFF2 subset and `html`/`body` resets. The preview renders the
 * same tree as a plain subtree of the dashboard's `<body>`, where it inherited Poppins and MUI
 * CssBaseline's `letter-spacing: 0.00938em` instead — 13-33% wider text, so copy that fits the
 * served ad was shown truncated and flagged "shortens". Measured in the render-parity lab
 * (2026-09-26): with this font and these resets on the ad root, 90/90 text boxes match the served
 * ad to 0.00px on every template and size.
 *
 * Pure on purpose (no React, no asset import): the font URL is passed in by `DisplayTemplate`, so
 * this module can be tested in node. Every rule is scoped to `.tpl-ad-root`; the rest of the
 * dashboard keeps Poppins.
 *
 * The font file is a byte copy of the backend's; `AD_FONT_SHA256` is pinned by a test in each repo.
 * Update both together.
 */

export const AD_ROOT_CLASS = "tpl-ad-root";

export const AD_FONT_SHA256 =
  "5834c243b46cd9bee9bae1117caebdc51450dc4a3c2ab19439486339e2e178db";

/** The tag's `html, body { font-family }`. The fallbacks are condensed too, like the tag's. */
export const AD_FONT_STACK =
  '"Roboto Condensed", "Arial Narrow", "Liberation Sans Narrow", Arial, sans-serif';

/**
 * The tag's `html` and `body` rules, applied to the ad root as an **inheritance reset**: every
 * value a node inside the ad could inherit is pinned to what the served page gives it, whatever
 * the host page (MUI theme, a centred table cell) has set above. `margin` and `background-color`
 * are left out: they belong to the served page's body, not to the ad. Properties the tag does not
 * set (`word-spacing`, `overflow-wrap`, `word-break`, ...) are pinned to their initial values, which
 * is what the served page gives them.
 */
export const AD_ROOT_DECLARATIONS: ReadonlyArray<readonly [string, string]> = [
  ["font-family", AD_FONT_STACK],
  ["font-size", "1rem"],
  ["font-weight", "400"],
  ["font-style", "normal"],
  ["line-height", "1.5"],
  ["letter-spacing", "normal"],
  ["word-spacing", "normal"],
  ["text-transform", "none"],
  ["text-indent", "0"],
  ["text-align", "start"],
  ["white-space", "normal"],
  ["overflow-wrap", "normal"],
  ["word-break", "normal"],
  ["color", "rgba(0, 0, 0, 0.87)"],
  ["-webkit-font-smoothing", "antialiased"],
  ["-moz-osx-font-smoothing", "grayscale"],
  ["-webkit-text-size-adjust", "100%"],
  ["font-synthesis", "none"],
  ["text-rendering", "optimizeLegibility"],
  ["box-sizing", "border-box"],
];

/** The stylesheet text: the tag's `@font-face` plus the scoped resets. */
export function adRootCss(fontUrl: string): string {
  const root = AD_ROOT_DECLARATIONS.map(([prop, value]) => `  ${prop}: ${value};`).join("\n");
  const scope = `.${AD_ROOT_CLASS}`;
  return [
    "@font-face {",
    '  font-family: "Roboto Condensed";',
    `  src: url("${fontUrl}") format("woff2");`,
    "  font-weight: 400 700;",
    "  font-style: normal;",
    "  font-display: block;",
    "}",
    `${scope} {`,
    root,
    "}",
    `${scope} *, ${scope} *::before, ${scope} *::after {`,
    "  box-sizing: inherit;",
    "  text-decoration: none;",
    "}",
  ].join("\n");
}

const STYLE_ELEMENT_ID = "tpl-ad-root-styles";

/**
 * Adds the stylesheet to the document once. Idempotent, and a no-op without a DOM.
 *
 * A single `<style>` in `<head>` rather than a per-instance `GlobalStyles`: a creative list renders
 * dozens of previews, and the `@font-face` must be registered in the document itself, because the
 * fit rig measures with canvas `measureText`, which only sees document fonts.
 */
export function installAdRootStyles(fontUrl: string): void {
  if (typeof document === "undefined") return;
  if (document.getElementById(STYLE_ELEMENT_ID)) return;
  const style = document.createElement("style");
  style.id = STYLE_ELEMENT_ID;
  style.textContent = adRootCss(fontUrl);
  document.head.appendChild(style);
}

/**
 * Which type environment a preview renders in.
 *
 * - `served-v2`: the ad root above (Roboto Condensed, `letter-spacing: normal`). What the backend's
 *   tag serves since `440820cb`, and what every creative the studio builds will serve.
 * - `legacy`: no ad root, so the ad inherits the dashboard's Poppins and MUI's `0.00938em`, exactly as
 *   before render parity Stage 0. That matches the old tag (`v2-prod`): Poppins from Google Fonts,
 *   `letter-spacing: 0.00938em`. The tag is generated once, at create, so creatives made before the
 *   new tag keep serving Poppins.
 */
export type AdFont = "served-v2" | "legacy";

/**
 * The new tag's own `@font-face`: the Roboto Condensed family **with its source inlined as a WOFF2
 * data URI** (`creativeTemplate.generateTag.model.js`). The family name alone is not enough, since a
 * third-party tag could name the family without serving this face.
 *
 * Tolerant of spellings the backend could switch to without changing what it serves: single,
 * double or no quotes around the family (matched pairs only) and around the `url(...)`, other
 * declarations before or between the two, any whitespace. Pinned to the generator's real output by
 * `__tests__/fixtures/servedTags.ts`.
 */
export const SERVED_V2_TAG_MARKER =
  /@font-face\s*\{[^}]*?font-family\s*:\s*(["']?)Roboto Condensed\1\s*;[^}]*?src\s*:\s*url\(\s*["']?data:font\/woff2;base64,/;

/** The font a stored creative serves, from its tag (`DisplayAds.creativeContentTag`). */
export function adFontOfTag(tag: string | null | undefined): AdFont {
  return tag && SERVED_V2_TAG_MARKER.test(tag) ? "served-v2" : "legacy";
}
